import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { isMobileUA, makeSequence } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const IS_MOBILE_UA = isMobileUA();
const IS_LT_MD = isLessThanBreakpoint('md');
const PUPPY_THUMBNAIL_SIZE = IS_LT_MD ? '180px' : '260px';

function PuppyCardSkeleton() {
  return (
    <div
      className={classnames('PuppyCardSkeleton br3 overflow-hidden', {
        'ba b--default': IS_MOBILE_UA
      })}
    >
      <SkeletonBlock
        className="mb4 br3-ns"
        height={IS_LT_MD ? 'calc(100vw - 48px)' : '335px'}
      />
      <div className={classnames({ 'ph4 pb4': IS_MOBILE_UA })}>
        <SkeletonBlock className="font-20 mb2" height="1em" width="6em" />
        <div className="flex items-center gap-8 mb2">
          <SkeletonBlock className="br-100" height="24px" width="24px" />
          <SkeletonBlock className="font-14" height="1em" width="16em" />
        </div>
        <SkeletonBlock className="font-14" height="1em" width="8em" />
      </div>
    </div>
  );
}

function BreederCardSkeleton() {
  return (
    <div className="BreederCardSkeleton br3 overflow-hidden ba b--default">
      <div className="flex items-center gap-12 md:gap-16 pt4 ph4 mb6">
        <SkeletonBlock className="br-100" height="46px" width="46px" />
        <div>
          <SkeletonBlock
            className="font-18 md:font-20 mb2"
            height="1em"
            width="10em"
          />
          <SkeletonBlock className="font-12" height="1em" width="16em" />
        </div>
      </div>
      <div className="flex gap-8 overflow-hidden pl4">
        {makeSequence(3).map(index => (
          <SkeletonBlock
            key={index}
            className="br3 w-100 flex-none"
            height={PUPPY_THUMBNAIL_SIZE}
            width={PUPPY_THUMBNAIL_SIZE}
          />
        ))}
      </div>
      <div className="flex flex-column gap-8 mh4 mt4 pb4">
        <SkeletonBlock className="font-14" height="1em" width="100%" />
        <SkeletonBlock className="font-14" height="1em" width="50%" />
      </div>
      <div className="flex-ns gap-20 justify-between mh4 pv4 items-center f2 bt b--default">
        <SkeletonBlock className="font-14" height="1em" width="12em" />
        <SkeletonBlock
          className="pv2 lg:font-14 ml-auto w-100 w-auto-ns mt3 mt0-md"
          height="2em"
          minWidth="8em"
        />
      </div>
    </div>
  );
}

export default function SearchResultsSkeleton({
  className,
  resultsType,
  resultsPerPage
}) {
  const CardSkeleton =
    resultsType === RESULTS_TYPES.BREEDERS
      ? BreederCardSkeleton
      : PuppyCardSkeleton;

  return (
    <div className={classnames('SearchResultsSkeleton', className)}>
      <ul className="row">
        {makeSequence(resultsPerPage).map(index => (
          <li
            key={index}
            className={classnames('mb8 mb10-md col-12 col-md-6', {
              'col-xl-3': resultsType === RESULTS_TYPES.PUPPIES
            })}
          >
            <CardSkeleton />
          </li>
        ))}
      </ul>
    </div>
  );
}

SearchResultsSkeleton.propTypes = {
  className: PropTypes.string,
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
  resultsPerPage: PropTypes.number.isRequired
};

SearchResultsSkeleton.defaultProps = {
  className: null
};
